import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUsername } from "@/hooks/useUsername";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Loader2, X } from "lucide-react";
import {
  USERNAME_MAX,
  USERNAME_MIN,
  checkUsernameAvailable,
  claimUsername,
  validateUsernameFormat,
} from "@/lib/username";
import { toast } from "sonner";
import { AppSeo } from "@/components/AppSeo";

type Status = "idle" | "checking" | "available" | "taken" | "invalid";

const UsernameOnboarding = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { username: existing, resolvedForPersona, refresh } = useUsername();
  const returnTo = params.get("returnTo") || "/dashboard";

  // Pre-fill from the desired_username captured at signup (set in user_metadata
  // by Auth.tsx). Saves the user from re-typing after email verification.
  const desiredFromMetadata = (() => {
    const raw = (user?.user_metadata as { desired_username?: unknown } | undefined)?.desired_username;
    if (typeof raw !== "string") return "";
    const v = validateUsernameFormat(raw);
    return v.ok ? v.normalized : "";
  })();
  const [value, setValue] = useState(desiredFromMetadata);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Set to true the moment our own submit succeeds so the redirect-on-existing
  // effect below doesn't race with the explicit navigate() in onSubmit.
  const justClaimedRef = useRef(false);

  // Bounce away if no user, or if they already have a username (e.g. arrived
  // here directly with a handle already set in another tab).
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/auth?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
      return;
    }
    if (justClaimedRef.current) return;
    // Only bounce away when we have a definitive answer for the CURRENT
    // active Persona — not on a transient/stale `existing` value from a
    // prior Persona, which would loop with ProtectedRoute.
    if (resolvedForPersona && existing) {
      navigate(returnTo, { replace: true });
    }
  }, [authLoading, user, resolvedForPersona, existing, navigate, returnTo]);

  const validation = useMemo(() => validateUsernameFormat(value), [value]);

  // Debounced availability check.
  const debounceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!validation.ok) {
      setStatus(value.length === 0 ? "idle" : "invalid");
      setErrorMessage(value.length === 0 ? null : (validation as { ok: false; message: string }).message);
      return;
    }
    setStatus("checking");
    setErrorMessage(null);
    debounceRef.current = window.setTimeout(async () => {
      const available = await checkUsernameAvailable(validation.normalized);
      setStatus(available ? "available" : "taken");
      if (!available) setErrorMessage("That username is taken or reserved.");
    }, 350);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value, validation]);

  const { activePersonaId } = useWorkspace();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status !== "available" || !validation.ok || !activePersonaId) return;
    setSubmitting(true);
    try {
      await claimUsername(activePersonaId, validation.normalized);
      // Once the user has a username they're a fully provisioned account —
      // forward any captured `?ref=` to the server (covers Google OAuth and
      // backfill paths where signup didn't run through Auth.tsx).
      try {
        const { attributeSignupFromStorage } = await import("@/lib/affiliateAttribution");
        await attributeSignupFromStorage();
      } catch (err) {
        console.warn("[onboarding] attribution forwarding failed", err);
      }
      justClaimedRef.current = true;
      toast.success(`Welcome, @${validation.normalized}`);
      // Await the refresh so the username hook (and ProtectedRoute on the
      // destination route) sees the freshly-claimed handle for this Persona
      // before we navigate. Skipping the await caused a redirect loop back
      // to /onboarding/username when the destination re-checked too soon.
      await refresh();
      navigate(returnTo, { replace: true });
      return;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Couldn't claim that username.";
      setErrorMessage(message);
      setStatus("taken");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <AppSeo title="Choose a username" />
    <div className="min-h-dvh flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="font-serif text-4xl font-semibold text-primary">Pick your personal handle</h1>
          <p className="text-muted-foreground mt-2">
            This is <strong>your</strong> handle as a person on Excerptus — it powers your
            affiliate link (<span className="font-mono">?ref=@you</span>) and is reserved for
            your future personal profile (<span className="font-mono">/u/@you</span>).
          </p>
        </div>

        <Card className="shadow-elevated">
          <CardHeader>
            <CardTitle>Choose your personal username</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Building under a brand?</span>{" "}
              You'll claim a separate <span className="font-mono">@brand_handle</span> for{" "}
              <strong>each workspace</strong> in your plan from Settings → Workspace after
              onboarding. Use this slot for <strong>your own name</strong>.
            </div>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">@</span>
                  <Input
                    id="username"
                    autoFocus
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={value}
                    onChange={(e) => setValue(e.target.value.toLowerCase().slice(0, USERNAME_MAX))}
                    className="pl-7 pr-9"
                    aria-invalid={status === "invalid" || status === "taken"}
                    placeholder="yourhandle"
                    disabled={submitting}
                  />
                  <span className="absolute inset-y-0 right-3 flex items-center">
                    {status === "checking" ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : status === "available" ? (
                      <Check className="h-4 w-4 text-primary" />
                    ) : status === "taken" || status === "invalid" ? (
                      <X className="h-4 w-4 text-destructive" />
                    ) : null}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {USERNAME_MIN}–{USERNAME_MAX} lowercase letters and numbers. Must include at least one letter.
                </p>
                {errorMessage ? (
                  <p className="text-sm text-destructive">{errorMessage}</p>
                ) : status === "available" ? (
                  <p className="text-sm text-primary">@{validation.ok ? validation.normalized : ""} is available.</p>
                ) : null}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={status !== "available" || submitting}
              >
                {submitting ? "Claiming…" : "Claim username"}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                You can rename your personal handle once every 12 months, so pick something
                you'll be happy with for a while.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default UsernameOnboarding;
